import { RefObject, useEffect } from 'react';

import {
  hotkeyPrimaryOrOptionalCodeMatches,
  isInsideOrAroundScope,
  isTypingTarget,
  keyboardModifiersMatchHotkeyBase,
} from './hotkeyFunctions.ts';
import { formatHotkeyLabel } from './hotkeyLabels.ts';
import { Key } from './hotkeys/KeyStrokes.ts';
import { HotkeyBase, InventoryHotkeyNames } from './hotkeys/types.ts';

/**
 * The inventory page shortcuts. Single source of truth: {@link useInventoryHotkeys} matches on
 * these, {@link inventoryShortcutLabel} names them in tooltips, and
 * `InventoryShortcuts` presents them in the shortcuts dialog.
 */
export const DefaultInventoryHotKeys: Record<InventoryHotkeyNames, HotkeyBase> = {
  [InventoryHotkeyNames.FocusSearchKey]: {
    primary: Key.KeyK,
    hotkeyModifier: { isModifierIconLast: true, modifier: 'Shift' },
  },
  /** Unmodified, and only reached when focus sits outside a text field (see `isTypingTarget`). */
  [InventoryHotkeyNames.RestoreFocusKey]: {
    primary: Key.navigation.Escape,
  },
  [InventoryHotkeyNames.ToggleFlatListKey]: {
    primary: Key.KeyF,
    hotkeyModifier: { isModifierIconLast: true, modifier: 'Shift' },
  },
  [InventoryHotkeyNames.ToggleHideLockedKey]: {
    primary: Key.KeyL,
    hotkeyModifier: { isModifierIconLast: true, modifier: 'Shift' },
  },
};

/** Keycap text for an inventory shortcut, e.g. `Shift+F`, for tooltips and adornments. */
export const inventoryShortcutLabel = (name: InventoryHotkeyNames): string =>
  formatHotkeyLabel(DefaultInventoryHotKeys[name]);

type UseInventoryHotkeysProps = {
  /** Inventory page root; shortcuts stay quiet while focus is elsewhere in the app. */
  scopeRef: RefObject<HTMLElement | null>;
  /** Omit a handler to leave that shortcut unbound. */
  onFocusSearch?: () => void;
  /** Escape: put focus back where the user left it after a click dropped it on the page chrome. */
  onRestoreFocus?: () => void;
  onToggleFlatList?: () => void;
  onToggleHideLocked?: () => void;
};

/**
 * Registers the inventory page shortcuts while mounted, scoped to `scopeRef` and ignored while
 * typing.
 */
export const useInventoryHotkeys = ({
  scopeRef,
  onFocusSearch,
  onRestoreFocus,
  onToggleFlatList,
  onToggleHideLocked,
}: UseInventoryHotkeysProps) => {
  useEffect(() => {
    const handlers: Record<InventoryHotkeyNames, (() => void) | undefined> = {
      [InventoryHotkeyNames.FocusSearchKey]: onFocusSearch,
      [InventoryHotkeyNames.RestoreFocusKey]: onRestoreFocus,
      [InventoryHotkeyNames.ToggleFlatListKey]: onToggleFlatList,
      [InventoryHotkeyNames.ToggleHideLockedKey]: onToggleHideLocked,
    };

    const onKeyDown = (ev: KeyboardEvent) => {
      if (!isInsideOrAroundScope(scopeRef)) return;
      if (isTypingTarget(ev.target)) return;
      if (ev.repeat) return;

      for (const [name, hotkey] of Object.entries(DefaultInventoryHotKeys) as [
        InventoryHotkeyNames,
        HotkeyBase,
      ][]) {
        const handler = handlers[name];
        if (!handler) continue;
        if (!hotkeyPrimaryOrOptionalCodeMatches(hotkey, ev.code)) continue;
        if (!keyboardModifiersMatchHotkeyBase(ev, hotkey)) continue;

        ev.preventDefault();
        handler();
        return;
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [scopeRef, onFocusSearch, onRestoreFocus, onToggleFlatList, onToggleHideLocked]);
};
